import { Component, OnInit, ChangeDetectionStrategy, Input } from '@angular/core';
import { closeEmailConversation } from 'common/store/genesys-cloud/conversations/emails/actions/email-conversation.actions';
import { EmailMessage } from 'common/store/genesys-cloud/conversations/emails/models/email-message.model';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { GenesysEmailFooterStore } from '../genesys-email-footer.store';
import { ClosingConfirmationService } from 'shared/components/confirmation-modal/confirmation-modal.service';
import { Store } from '@ngrx/store';
import { EmailAddress } from 'common/store/genesys-cloud/conversations/emails/models/email-address.model';
import { Actions, ofType } from '@ngrx/effects';
import * as EmailMessageActions from 'common/store/genesys-cloud/conversations/emails/actions/email-message.actions';
import { filter, map } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import {
  selectEmailError,
  selectIsConversationOpen,
  selectIsConversationTransmitting
} from 'common/store/genesys-cloud/conversations/emails/selectors/email-conversation.selectors';
import { GenesysEmailOutgoingHeader } from 'shared/components/genesys-email-outgoing-header/models/genesys-email-outgoing-header.model';
import { DestroySubjectBaseDirective } from 'shared/directives/destroy-subject-base/destroy-subject-base.directive';
import moment from 'moment';
import { GenesysEmailComposeService } from '../services/genesys-email-compose.service';

@Component({
  selector: 'app-genesys-proactive-email',
  templateUrl: './genesys-proactive-email.component.html',
  styleUrls: ['./genesys-proactive-email.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GenesysEmailFooterStore]
})
export class GenesysProactiveEmailComponent extends DestroySubjectBaseDirective implements OnInit {
  readonly editorMessages$ = this._emailFooterStore.editorMessages$;
  readonly composedEmail$ = this._emailFooterStore.composedEmail$;
  readonly isConfirmationModalVisible$ = this._emailFooterStore.isConfirmationModalVisible$;

  @Input() conversationId: string;
  @Input() isFocused: boolean;
  @Input() draftEmail: EmailMessage;
  @Input() agentEmailAddress: EmailAddress;
  @Input() customerEmailAddresses: EmailAddress[];

  isConversationOpen$: Observable<boolean>;
  isInteractionDisabled$: Observable<boolean>;

  constructor(
    private readonly _emailFooterStore: GenesysEmailFooterStore,
    private readonly _closingConfirmationService: ClosingConfirmationService,
    private readonly store: Store,
    private readonly _actions: Actions,
    private readonly _emailComposeService: GenesysEmailComposeService
  ) {
    super();
  }

  ngOnInit(): void {
    this._emailFooterStore.initConversationId(this.conversationId);
    const defaultComposedEmail = this._emailComposeService.getDefaultComposedEmail(
      this.draftEmail,
      this.agentEmailAddress
    );
    this._emailFooterStore.initComposedEmail({ ...defaultComposedEmail, to: this.customerEmailAddresses });

    this.isConversationOpen$ = this.store.select(selectIsConversationOpen(this.conversationId));

    this._initEmailError();
    this._initInteractionsDisabled();
    this._initActionSubscriptions();
  }

  onHeaderValidate(result: boolean): void {
    this._emailFooterStore.setHasValidEmailRecipients(result);
  }

  clearEditorMessages(): void {
    this._emailFooterStore.clearEditorMessages();
  }

  editorContentChanged({ htmlBody, textBody }: { htmlBody: string; textBody: string }): void {
    this._emailFooterStore.storeComposedEmail({ htmlBody, textBody });
  }

  emailHeaderChange(model: GenesysEmailOutgoingHeader): void {
    this._emailFooterStore.storeComposedEmail({ ...model });
  }

  handleConfirmation(): void {
    const isConfirmationModalDisabled = <boolean>(
      this._closingConfirmationService.getClosingConfirmationSetting(WorkItemTypes.GENESYS_EMAIL)
    );
    if (isConfirmationModalDisabled) {
      this.store.dispatch(closeEmailConversation({ conversationId: this.conversationId }));
    } else {
      this._emailFooterStore.setIsConfirmationModalVisible(true);
    }
  }

  closeConfirmation(): void {
    this._emailFooterStore.setIsConfirmationModalVisible(false);
  }

  onSend(composedEmail: Partial<EmailMessage>): void {
    const emailMessage = {
      ...composedEmail,
      attachments: this.draftEmail?.attachments || [],
      time: moment().toISOString(),
      historyIncluded: false
    };

    const emailError = this._emailComposeService.getEmailValidatorErrorMessage(emailMessage);
    if (emailError) {
      this._emailFooterStore.setEditorMessages(emailError);
    } else {
      this.store.dispatch(EmailMessageActions.sendEmailMessage({ conversationId: this.conversationId, emailMessage }));
    }
  }

  onAttachmentSizeLimitError(sizeLimit: number): void {
    this._emailFooterStore.setEditorMessages('bcEmailBox.attachmentOverSizeLimitMessage', {
      sizeLimit
    });
  }

  private _initActionSubscriptions(): void {
    const filterConversation = ({ conversationId }: { conversationId: string; errorMessage?: string }) =>
      conversationId === this.conversationId;

    this._actions
      .pipe(ofType(EmailMessageActions.sendEmailMessage), filter(filterConversation), this.takeUntilDestroy())
      .subscribe(() => {
        this._emailFooterStore.setEditorMessages('genesysProactiveEmail.sendingEmail');
        this._emailFooterStore.setInteractionsEnabled(false);
      });

    this._actions
      .pipe(ofType(EmailMessageActions.sendEmailMessageFailure), filter(filterConversation), this.takeUntilDestroy())
      .subscribe(({ errorMessage }) => {
        this._emailFooterStore.setEditorMessages(errorMessage);
        this._emailFooterStore.setInteractionsEnabled(true);
      });

    this._actions
      .pipe(ofType(EmailMessageActions.attachmentUploadSuccess), filter(filterConversation), this.takeUntilDestroy())
      .subscribe(() => this._emailFooterStore.removeEditorMessage('genesysProactiveEmail.uploadingAttachment'));

    this._actions
      .pipe(ofType(EmailMessageActions.attachmentUploadFailure), filter(filterConversation), this.takeUntilDestroy())
      .subscribe(() => this._emailFooterStore.setEditorMessages('genesysProactiveEmail.attachmentUploadError'));

    this._actions
      .pipe(ofType(EmailMessageActions.attachmentDeleteFailure), filter(filterConversation), this.takeUntilDestroy())
      .subscribe(() => this._emailFooterStore.setEditorMessages('genesysProactiveEmail.attachmentDeleteError'));

    this._actions
      .pipe(ofType(EmailMessageActions.uploadAttachment), filter(filterConversation), this.takeUntilDestroy())
      .subscribe(() => this._emailFooterStore.setEditorMessages('genesysProactiveEmail.uploadingAttachment'));
  }

  private _initInteractionsDisabled(): void {
    const isTransmitting$ = this.store.select(selectIsConversationTransmitting(this.conversationId));

    this.isInteractionDisabled$ = combineLatest([
      isTransmitting$,
      this.isConversationOpen$,
      this._emailFooterStore.isInteractionsEnabled$,
      this._emailFooterStore.hasValidEmailRecipients$
    ]).pipe(
      map(
        ([isTransmitting, isConversationOpen, isInteractionsEnabled, hasValidEmailRecipients]) =>
          isTransmitting || !isConversationOpen || !isInteractionsEnabled || !hasValidEmailRecipients
      )
    );
  }

  private _initEmailError(): void {
    const emailError$ = this.store.select(selectEmailError(this.conversationId));

    emailError$.pipe(filter(Boolean), this.takeUntilDestroy()).subscribe((errorMessage) => {
      this._emailFooterStore.setEditorMessages(errorMessage);
      this._emailFooterStore.setInteractionsEnabled(true);
    });
  }
}
